import qs from 'qs'
import React, { useCallback, useContext, useMemo } from 'react'

import { AddDetailsCopy } from './Common'
import { PlaceInputTrigger } from './Common/SearchFields'
import SearchPlace from './SearchPlace'
import { ItemMetadata } from '../../../../models'
import CreateActivity from '../Create/CreateActivity'
import CreateAttraction from '../Create/CreateAttraction'
import CreateEvent from '../Create/CreateEvent'
import CreateRestaurantBar from '../Create/CreateRestaurantOrBar'
import CreateThingToDo from '../Create/CreateThingToDo'

import { addToTripClickEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import Hr from 'components/Common/Hr'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { useTripMetadataPrefetch } from 'tripPlanner/hooks/api'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { TripItem } from 'tripPlanner/types/tripItem'

const SUB_TYPES: TripItem['type'][] = [
  'ACTIVITY',
  'RESTAURANT_BAR',
  'ATTRACTION',
  'EVENT',
  'THING_TO_DO',
]

type SubType = (typeof SUB_TYPES)[number]

interface Props {
  tripId: string
  sectionId?: string
}

function SearchExperience({ tripId, sectionId }: Props) {
  useTripMetadataPrefetch({ tripId })
  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()
  const { setImmersiveMode } = useImmersiveModeHandlers()

  const { searchItem } = useContext(GlobalSearchStateContext)
  const source = useTripClientSource()
  const query = useMemo(
    () =>
      qs.stringify({
        destinationId: searchItem
          ? searchItem.value
          : ANYWHERE_SEARCH_ITEM.value,
        destinationName: searchItem
          ? searchItem.format.mainText
          : ANYWHERE_SEARCH_ITEM.format.mainText,
        source,
      }),
    [searchItem, source],
  )

  const onPrimaryActionClick = useCallback(() => {
    fireInteractionEvent(addToTripClickEvent('experiences_search'))
    setImmersiveMode({ tripId, sectionId })
  }, [setImmersiveMode, sectionId, tripId])

  const addItem = useCallback(
    (type: SubType) => {
      switch (type) {
        case 'ACTIVITY':
          openModal(CreateActivity, { tripId })
          break
        case 'RESTAURANT_BAR':
          openModal(CreateRestaurantBar, { tripId })
          break
        case 'ATTRACTION':
          openModal(CreateAttraction, { tripId })
          break
        case 'EVENT':
          openModal(CreateEvent, { tripId })
          break
        case 'THING_TO_DO':
          openModal(CreateThingToDo, { tripId })
      }
    },
    [openModal, tripId],
  )

  return (
    <>
      <ModalHeader
        title="Things to do"
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={16}>
            <BodyText variant="large">
              <BodyText as="span" variant="large" weight="semi-bold">
                Need inspiration?{' '}
              </BodyText>
              Search our exclusive Luxury Escapes offers
            </BodyText>
            <PlaceInputTrigger
              subTitle={searchItem?.format.mainText || 'Search by destination'}
              onClick={() =>
                openModal(
                  SearchPlace,
                  {
                    searchView: 'EXPERIENCE' as const,
                    tripId,
                  },
                  {
                    height: 'full',
                  },
                )
              }
            />
            <TextButton
              kind="primary"
              fit="flex"
              to={'/search/experiences?' + query}
              onClick={onPrimaryActionClick}
              size="large"
            >
              Search
            </TextButton>
            <Group
              direction="horizontal"
              horizontalAlign="space-between"
              gap={16}
              verticalAlign="center"
            >
              <Hr size="sm" />
              <BodyText variant="medium" colour="neutral-two">
                or
              </BodyText>
              <Hr size="sm" />
            </Group>
            <AddDetailsCopy>
              <BodyText variant="large">Add your own details</BodyText>
              <LabelGroup>
                {SUB_TYPES.map((i) => {
                  const { icon: Icon, name } = ItemMetadata[i]
                  return (
                    <FilterChip
                      size="medium"
                      onClick={() => addItem(i)}
                      key={name}
                      startIcon={<Icon />}
                    >
                      {name}
                    </FilterChip>
                  )
                })}
              </LabelGroup>
            </AddDetailsCopy>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SearchExperience
